import React, { useState, useEffect, useCallback, memo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { fromJS, List, Map } from 'immutable';

import { getModuleSuggestions } from '../../actions/popup/windwardReportsPopup';
import Popups from '../../components/Popup';
import { clearWindwardTemplate, uploadTemplate, handleUpdateWindwardTemplates } from '../../actions/windward';
import { usePopupContext } from '../../contexts/PopupContext';

/** Upload windward templates modal component. */
const UploadTemplatePopupContainer = ({
  popup,
  isValid,
  isUploading,
  inputErrors,
  getModuleSuggestion,
  clearWindwardTemplate,
  handleUpdateWindwardTemplates,
}) => {
  const { closePopup } = usePopupContext();
  const [fileName, setFileName] = useState(null);
  const [module, setModule] = useState({
    suggestions: List(),
    pagination: {},
  });
  const [moduleLoading, setModuleLoading] = useState(false);
  const [moduleText, setModuleText] = useState('');
  const [selectedUserId, setSelectedUserId] = useState(null);

  useEffect(
    () => () => {
      clearWindwardTemplate();
    },
    [],
  );

  const updateModuleSuggestion = useCallback(suggestions => {
    if (!suggestions || !Object.prototype.hasOwnProperty.call(suggestions, 'data')) {
      return;
    }

    setModuleLoading(false);
    setModule({
      suggestions: fromJS([...suggestions.data]),
      pagination: suggestions.meta && suggestions.meta.pagination,
    });
  }, []);

  const handleClose = useCallback(() => {
    closePopup();
  }, [closePopup]);

  const handleSelectFile = useCallback(
    event => {
      const { name } = event.target;
      const file = event.target.files.item(0);

      setFileName(file.name);

      return handleUpdateWindwardTemplates({
        name,
        value: file.name,
      });
    },
    [handleUpdateWindwardTemplates],
  );

  const handleGetSuggestionForModule = useCallback(
    ({ value }) => {
      setModuleLoading(true);
      getModuleSuggestion(value).then(response => updateModuleSuggestion(response));
    },
    [getModuleSuggestion, updateModuleSuggestion],
  );

  const handleSuggestionSelected = useCallback(
    (event, { suggestion }) => {
      if (selectedUserId === suggestion.id) {
        return;
      }

      handleUpdateWindwardTemplates({
        name: 'module',
        value: suggestion.id,
      });

      setModuleText(suggestion.text);
      setSelectedUserId(suggestion.id);
    },
    [selectedUserId, handleUpdateWindwardTemplates],
  );

  const handleGetNextSuggestionForModule = useCallback(
    ({ value }) => {
      const { pagination } = module;

      if (pagination && pagination.currentPage < pagination.totalPages) {
        setModuleLoading(true);
        getModuleSuggestion(value).then(response => updateModuleSuggestion(response));
      }
    },
    [module, getModuleSuggestion, updateModuleSuggestion],
  );

  const handleUpdateFilter = useCallback(
    suggestion => {
      if (Object.prototype.hasOwnProperty.call(suggestion, 'text')) {
        setModuleText(suggestion.text);
        handleGetSuggestionForModule({ value: suggestion.text });
      }
    },
    [handleGetSuggestionForModule],
  );

  const handleUploadTemplate = useCallback(
    event => {
      const onUpload = popup.getIn(['props', 'onUpload']);

      onUpload(event, selectedUserId, fileName);
    },
    [popup, selectedUserId, fileName],
  );

  const handleRenderSuggestion = useCallback(suggestion => <div>{suggestion.text}</div>, []);

  const handleGetSuggestionValue = useCallback(suggestion => suggestion.id, []);

  const moduleProps = {
    suggestions: module.suggestions,
    moduleLoading,
    moduleText,
    getSuggestion: handleGetSuggestionForModule,
    getNextSuggestion: handleGetNextSuggestionForModule,
    updateSuggestions: handleUpdateFilter,
    renderSuggestion: handleRenderSuggestion,
    getSuggestionValue: handleGetSuggestionValue,
    suggestionSelected: handleSuggestionSelected,
  };

  const preparedErrors = Object.values(inputErrors.toJS()).map(error => error.replace(/[\r\n]/g, ''));

  return (
    <Popups.UploadTemplatePopup
      fileName={fileName}
      id="uploadTemplatePopup"
      inputErrors={fromJS([...preparedErrors])}
      isUploading={isUploading}
      isValid={isValid}
      onClose={handleClose}
      onSelectFile={handleSelectFile}
      onSelectModule={handleGetSuggestionForModule}
      onUpload={handleUploadTemplate}
      {...moduleProps}
    />
  );
};

UploadTemplatePopupContainer.propTypes = {
  clearWindwardTemplate: PropTypes.func.isRequired,
  getModuleSuggestion: PropTypes.func.isRequired,
  handleUpdateWindwardTemplates: PropTypes.func.isRequired,
  inputErrors: PropTypes.instanceOf(Map),
  isValid: PropTypes.bool,
};

const mapStateToProps = (state, props) => ({
  ...props,
  isUploading: state.windward.get('isUploading'),
  inputErrors: state.windward.get('inputErrors'),
  isValid: state.windward.get('isValid'),
});

const mapDispatchToProps = dispatch => ({
  getModuleSuggestion: (actionFrom, value) => dispatch(getModuleSuggestions(actionFrom, value)),
  ...bindActionCreators(
    {
      clearWindwardTemplate,
      uploadTemplate,
      handleUpdateWindwardTemplates,
    },
    dispatch,
  ),
});

export default connect(mapStateToProps, mapDispatchToProps)(memo(UploadTemplatePopupContainer));
